import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  ArrowLeft,
  Download,
  Printer,
  Mail,
  Phone,
  Globe,
  Calendar,
  User,
  FileText,
  DollarSign,
} from "lucide-react";
import { BlobProvider } from "@react-pdf/renderer";
import InvoicePDF from "./Utility/InvoicePDF";
import { useLazyGetInvoiceViewQuery } from '../State/Api/InvoiceApiSlice';

const InvoiceView = () => {
  const navigate = useNavigate();
  const location = useLocation();
    const { invoiceNumber, appointmentID } = useParams();

      const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
      const [triggerInvoiceView, { data: invoiceViewData, isLoading }] =
    useLazyGetInvoiceViewQuery();

      useEffect(() => {
    if (invoiceNumber && appointmentID) {
      triggerInvoiceView({ invoiceNumber, appointmentId: appointmentID });
    }
  }, [invoiceNumber, appointmentID, triggerInvoiceView]);



  const invoiceData = invoiceViewData?.invoiceDetails || {};
  const invoiceItems = invoiceViewData?.invoiceItems || [];


  const display = (value) =>
    value !== undefined && value !== null && value !== "" ? value : "N/A";

  // Calculate totals from invoice items
  const calculateSubtotal = () => {
    return invoiceItems.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );
  };

  const calculateTax = () => {
    return invoiceItems.reduce((sum, item) => sum + (Number(item.gst) || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  // Prepare data for PDF using your existing field names
  const prepareInvoiceDataForPDF = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const total = invoiceData.totalAmount || calculateTotal();

    
    

    return {
      // Clinic/Company info
      clinicName: invoiceData.clinicName || "Medical Clinic",
      clinicAddress:
        invoiceData.clinicAdress || invoiceData.clinicAddress || "N/A", // Handle typo
      clinicEmail: invoiceData.clinicEmail || "N/A",
      clinicPhone: invoiceData.clinicContactNumber || "N/A",
      clinicWebsite: invoiceData.clinicWebsite || "N/A",

      // Invoice details
      invoiceNumber: invoiceData.invoiceNumber || `INV-${Date.now()}`,
      invoiceId: invoiceData.invoiceId || invoiceData.invoiceNumber || "N/A",
      invoiceDate: invoiceData.issuedDate || "N/A",
      dueDate: invoiceData.dueDate || "N/A",
      invoiceType: invoiceData.invoiceType || "N/A",
      poReference: invoiceData.poReference || "N/A",

      // Patient info
      patientName: invoiceData.patientName || "N/A",
      patientId: invoiceData.patientUserUniqueID || "N/A",
      patientDOB: invoiceData.patientDateofBirth || "N/A",
      patientPhone: invoiceData.patientContactNumber || "N/A",
      patientEmail: invoiceData.patientEmailAddress || "N/A",
      patientAddress: invoiceData.patientAddress || "N/A",

      // Doctor info
      doctorName: invoiceData.doctorName || "N/A",

      // Invoice items - map to PDF format
      invoiceItems: invoiceItems.map((item) => ({
        description: item.description || "Service",
        unitPrice: Number(item.price) || 0,
        quantity: Number(item.units) || 1,
        total: Number(item.amount) || 0,
        gst: Number(item.gst) || 0,
      })),

      appointmentFee: invoiceData.appointmentFee || 0,

      // Totals
      subtotal: subtotal,
      tax: tax,
      total: total,

      // Payment info
      paymentType: invoiceData.paymentType || "N/A",
      amountPaid: invoiceData.amountPaid || 0,

      // Additional info
      notes: invoiceData.notes || "",

      // Logo (you can add this field to your invoice data)
      logo: invoiceData.logo || null,
    };
  };

    // 🎨 Skeleton Loader
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-full max-w-9xl p-6 space-y-6 animate-pulse">
          <div className="h-10 w-1/3 bg-gray-300 rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-40 bg-gray-200 rounded-lg"></div>
            <div className="h-40 bg-gray-200 rounded-lg"></div>
          </div>
          <div className="h-96 bg-gray-200 rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  // CSS Variables for gradient colors
  const gradientStyles = `
    :root {
      --color-secondary: #10b981;
      --color-secondary-mid: #059669;
      --color-secondary-dark: #047857;
      --color-dark-light: #4b5563;
      --color-dark: #374151;
      --color-dark-deep: #1f2937;
    } 
    
    .gradient-button {
      background: linear-gradient(130deg, var(--color-secondary) 0%, var(--color-secondary-mid) 50%, var(--color-secondary-dark) 100%);
      color: white;
      border: none;
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
      transition: all 0.3s ease;
    }
    
    .gradient-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    }
    
    .gradient-dark-button {
      background: linear-gradient(135deg, var(--color-dark-light) 0%, var(--color-dark) 50%, var(--color-dark-deep) 100%);
      color: white;
      border: none;
      box-shadow: 0 4px 15px rgba(75, 85, 99, 0.3);
      transition: all 0.3s ease;
    }
    
    .gradient-dark-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(75, 85, 99, 0.4);
    }

    .invoice-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(229, 231, 235, 0.8);
      transition: all 0.3s ease;
    }

    .invoice-header {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border-radius: 16px 16px 0 0;
      border-bottom: 1px solid rgba(226, 232, 240, 0.8);
    }

    .info-card {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border-radius: 12px;
      border: 1px solid rgba(226, 232, 240, 0.8);
    }

    .table-header {
      background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    }

    .total-section {
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
      border: 1px solid rgba(34, 197, 94, 0.2);
    }

    .patient-avatar {
      background: linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-dark) 100%);
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
    }
  `;

  return (
    <div className="min-h-screen bg-white">
      <style>{gradientStyles}</style>

      <div className="max-w-9xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/Billing")}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 group"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-gray-800" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Invoice {display(invoiceData.invoiceNumber)}
              </h1>
              <p className="text-gray-600 mt-1">
                {display(invoiceData.clinicName)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <BlobProvider
              document={<InvoicePDF invoiceData={prepareInvoiceDataForPDF()} />}
            >
              {({ url, loading, error }) => (
                <button
                  onClick={() => {
                    if (url && !loading) {
                      // Open PDF in new window and trigger print
                      const printWindow = window.open(url, "_blank");
                      if (printWindow) {
                        printWindow.onload = () => {
                          printWindow.print();
                        };
                      }
                    }
                  }}
                  disabled={loading}
                  className="gradient-dark-button px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                  style={{ opacity: loading ? 0.7 : 1 }}
                >
                  <Printer className="w-4 h-4" />
                  {loading ? "Preparing..." : "Print"}
                </button>
              )}
            </BlobProvider>

            <BlobProvider
              document={<InvoicePDF invoiceData={prepareInvoiceDataForPDF()} />}
            >
              {({ url, loading, error }) => (
                <button
                  onClick={() => {
                    if (url && !loading) {
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `invoice-${
                        invoiceData.invoiceNumber || Date.now()
                      }.pdf`;
                      link.click();
                    }
                  }}
                  disabled={loading}
                  className="gradient-button px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                  style={{ opacity: loading ? 0.7 : 1 }}
                >
                  <Download className="w-4 h-4" />
                  {loading ? "Generating..." : "Download PDF"}
                </button>
              )}
            </BlobProvider>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Main Invoice Content */}
          <div className="xl:col-span-3">
            <div className="invoice-card overflow-hidden">
              {/* Invoice Header */}
              <div className="invoice-header p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {display(invoiceData.clinicName)}
                    </h2>
                    <p className="text-gray-600">
                      Professional Medical Services
                    </p>
                  </div>
                  <div className="text-right">
                    <h3 className="text-xl font-bold text-gray-900">
                      Invoice {invoiceData.invoiceId}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      Invoice Number: {invoiceData.invoiceNumber}
                    </p>
                  </div>
                </div>

                {/* Invoice Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="info-card p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Issued Date
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {invoiceData.issuedDate
                        ? dayjs(invoiceData.issuedDate).format("DD-MMM-YYYY")
                        : "N/A"}
                    </p>
                  </div>
                  <div className="info-card p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Due Date
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {invoiceData.dueDate
                        ? dayjs(invoiceData.dueDate).format("DD-MMM-YYYY")
                        : "N/A"}
                    </p>
                  </div>
                  <div className="info-card p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Invoice Type
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {invoiceData.invoiceType}
                    </p>
                  </div>
                  <div className="info-card p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        PO Reference
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {invoiceData.poReference}
                    </p>
                  </div>
                </div>
              </div>

              {/* From and To Section */}
              <div className="p-6 border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      From
                    </h4>
                    <div className="space-y-2">
                      <p className="font-semibold text-gray-900">
                        {display(invoiceData.clinicName)}
                      </p>
                      <p className="text-gray-600">
                        {display(invoiceData.clinicAdress)}
                      </p>

                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">
                          {display(invoiceData.clinicEmail)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">
                          {display(invoiceData.clinicContactNumber)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Globe className="w-4 h-4" />
                        <span className="text-sm">
                          {display(invoiceData.clinicWebsite)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">To</h4>
                    <div className="space-y-2">
                      <p className="font-semibold text-gray-900">
                        {display(invoiceData.patientName)}
                      </p>
                      <p className="text-gray-600">
                        {" "}
                        {invoiceData.patientDateofBirth
                          ? dayjs(invoiceData.patientDateofBirth).format(
                              "DD-MMM-YYYY"
                            )
                          : "N/A"}
                      </p>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">
                          {display(invoiceData.patientEmailAddress)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">
                          {display(invoiceData.patientContactNumber)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Globe className="w-4 h-4" />
                        <span className="text-sm">
                          {display(invoiceData.patientAddress)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items Table */}
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="table-header">
                        <th className="text-left p-4 font-semibold text-gray-900 rounded-l-lg">
                          Description
                        </th>
                        <th className="text-center p-4 font-semibold text-gray-900">
                          Units
                        </th>
                        <th className="text-right p-4 font-semibold text-gray-900">
                          Price
                        </th>
                        <th className="text-right p-4 font-semibold text-gray-900">
                          GST
                        </th>
                        <th className="text-right p-4 font-semibold text-gray-900 rounded-r-lg">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoiceItems || []).map((item, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="p-4 text-gray-900 font-medium">
                            {display(item.description)}
                          </td>
                          <td className="p-4 text-center text-gray-900">
                            {display(item.units)}
                          </td>
                          <td className="p-4 text-right text-gray-900">
                            ₹{display(item.price)}
                          </td>
                          <td className="p-4 text-right text-gray-900">
                            ₹{display(item.gst)}
                          </td>
                          <td className="p-4 text-right text-gray-900 font-semibold">
                            ₹{display(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total Amount Section */}
                <div className="mt-6 flex justify-end">
                  <div className="w-full max-w-md space-y-2">
                     <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Appointment:</span>
                      <span className="font-semibold">
                        ₹  {display(invoiceData.appointmentFee)}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">
                        ₹{calculateSubtotal().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Tax (GST):</span>
                      <span className="font-semibold">
                        ₹{calculateTax().toFixed(2)}
                      </span>
                    </div>
                    <div className="total-section p-4 rounded-xl">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-green-600" />
                          <span className="text-lg font-bold text-gray-900">
                            Total Amount
                          </span>
                        </div>
                        <span className="text-2xl font-bold text-green-600">
                          ₹{display(invoiceData.totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Note */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> There will be a late payment fee of
                    10% per annum calculated daily for payments made after the
                    due date.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Patient Information */}
            <div className="invoice-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Patient Information
                </h3>
              </div>

              <div className="text-center mb-6">
                <div className="patient-avatar w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900">
                  {display(invoiceData.patientName)}
                </h4>
                <p className="text-gray-600 mt-1">
                  ID: {display(invoiceData.patientUserUniqueID)}
                </p>
              </div>

              <div className="space-y-4">
                <div className="info-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Date of Birth
                    </span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">
                    {invoiceData.patientDateofBirth
                      ? dayjs(invoiceData.patientDateofBirth).format(
                          "DD-MMM-YYYY"
                        )
                      : "N/A"}
                  </p>
                </div>
                <div className="info-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Assigned Doctor
                    </span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">
                    {display(invoiceData.doctorName)}
                  </p>
                </div>
              </div>

              <button onClick={()=>navigate(`/ClinicPatientView/${invoiceData.patientUserUniqueID}?from=${encodeURIComponent(
              location.pathname + location.search
            )}`)} className="gradient-dark-button w-full px-4 py-3 rounded-lg font-medium mt-6">
                View Patient Profile
              </button>
            </div>

            {/* Payment Method */}
            <div className="invoice-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-green-50 rounded-xl">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Payment Method
                </h3>
              </div>

              <div className="space-y-3">
                {["Credit Card", "Bank Transfer", "Cash", "Insurance"].map(
                  (method) => (
                    <label
                      key={method}
                      className="flex items-center gap-3 p-3 info-card cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method}
                        checked={selectedPaymentMethod === method}
                        onChange={(e) =>
                          setSelectedPaymentMethod(e.target.value)
                        }
                        className="w-4 h-4 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {method}
                      </span>
                    </label>
                  )
                )}
              </div>

              <button className="gradient-button w-full px-4 py-3 rounded-lg font-medium mt-6">
                Process Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
